import type { RegisteredRouter, RouteIds } from '@tanstack/react-router'
import type { ReactNode } from 'react'

import { useFilters } from '@/utils/use-search-params'

import { FiltersStateContext, type FiltersStateContextValue } from './filters-state-context'

interface FiltersStateProviderProps<TId extends RouteIds<RegisteredRouter['routeTree']>> {
  routeId: TId
  children: ReactNode
}

/**
 * Binds the filter components to the search params of `routeId`. The cast to the
 * loose {@link FiltersStateContextValue} is contained here so the consuming
 * filter components stay route-agnostic and fully typed.
 */
export function FiltersStateProvider<TId extends RouteIds<RegisteredRouter['routeTree']>>({
  routeId,
  children,
}: FiltersStateProviderProps<TId>) {
  const { filters, setFilters } = useFilters(routeId)

  // `routeId` est générique : `useSearch()` rend ici l'union du search de TOUTES
  // les routes enregistrées — un type que TypeScript ne peut pas rapprocher du
  // contrat du contexte, et dont certains membres n'ont même pas de propriété en
  // commun avec lui. Chaque membre est donc élargi séparément, à sa place dans le
  // contrat ; c'est précisément le rétrécissement que la doc de ce composant
  // annonce, et il est confiné ici pour que les composants de filtre restent
  // agnostiques de la route et pleinement typés.
  //
  // Le garde-fou du rétrécissement est ailleurs : `useFiltersState` lève si le
  // provider est absent, et les deux routes qui montent ce provider déclarent bien
  // ces paramètres dans leur schéma de search.
  const value: FiltersStateContextValue = {
    filters: filters as FiltersStateContextValue['filters'],
    setFilters: setFilters as FiltersStateContextValue['setFilters'],
  }

  return <FiltersStateContext.Provider value={value}>{children}</FiltersStateContext.Provider>
}
